import Controller from "@ember/controller";
import { task, all } from "ember-concurrency";
import { action } from "@ember/object";
import { service } from "@ember/service";
import destroyRecord from "glesys-controlpanel/utils/destroy-record";
import { saveRecord } from "@warp-drive/legacy/compat/builders";
import uniqid from "glesys-controlpanel/utils/uniqid";

export default class LoadBalancerDetailsController extends Controller {
  @service intl;
  @service notification;
  @service store;

  get loadBalancer() {
    return this.model.loadBalancer;
  }

  get certificates() {
    return this.model.certificates;
  }

  get servers() {
    return this.model.servers;
  }

  onChange = ({ name }) => (this.loadBalancer.name = name);

  uploadCertificateTask = task(async (name, certificate) => {
    let loadBalancer = this.loadBalancer;

    let loadBalancerCertificate = this.store.createRecord("load-balancer-certificate", {
      id: name,
      certificate,
      loadBalancer,
      name,
    });

    await this.store.request(saveRecord(loadBalancerCertificate)).catch((error) => {
      loadBalancerCertificate.unloadRecord();
      throw error;
    });
  });

  deleteCertificateTask = task(async (certificate) => {
    await destroyRecord(certificate);
  });

  saveLoadBalancerTask = task(async () => {
    await this.store.request(saveRecord(this.loadBalancer));

    this.notification.add(
      "success",
      this.intl.t("load-balancer.notifications.load-balancer-updated.title"),
      this.intl.t("load-balancer.notifications.load-balancer-updated.text"),
    );
  });

  saveListenerTask = task(async (listener) => {
    await this.store.request(saveRecord(listener));
    await all((listener.dirtyTargets ?? []).map((target) => this.saveTarget(target)));
  });

  async saveTarget(target) {
    let { active, ...changedAttributes } = target.changedAttributes();
    let { isNew, isDeleted } = target;

    if (isNew || isDeleted || Object.keys(changedAttributes).length > 0) {
      await this.store.request(saveRecord(target));
    }

    if (active && !isNew && !isDeleted) {
      let adapter = this.store.adapterFor("load-balancer-target");
      let [, enable] = active;

      await (enable ? adapter.enable(target) : adapter.disable(target));
    }
  }

  saveListenersTask = task(async () => {
    await all(this.loadBalancer.dirtyListeners.map(this.saveListenerTask.perform));

    this.notification.add(
      "success",
      this.intl.t("load-balancer.notifications.load-balancer-updated.title"),
      this.intl.t("load-balancer.notifications.load-balancer-updated.text"),
    );
  });

  @action
  addTarget(listener) {
    this.store.createRecord("load-balancer-target", {
      active: true,
      ipaddress: "",
      name: uniqid("ta"),
      port: listener.get("port"),
      weight: 1,
      listener,
    });
  }

  @action
  addListener() {
    let loadBalancer = this.loadBalancer;

    this.store.createRecord("load-balancer-listener", {
      mode: "http",
      stickysessions: false,
      loadBalancer,
    });
  }
}
